'use client';

// Fires `landing_section_viewed` once when a landing section first scrolls into
// view — the named-section layer on top of PostHog's automatic $pageleave
// scroll-depth, so "where visitors drop while scrolling" reads as sections, not
// raw percentages. Renders a plain block wrapper (full-width, no styles) around
// its section, so it has no layout effect. Accepts a server-component child.

import { useEffect, useRef } from 'react';
import { track } from '@/lib/analytics';

export default function TrackSection({
  name,
  children,
}: {
  name: string;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const fired = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !fired.current) {
          fired.current = true;
          track('landing_section_viewed', { section: name });
          io.disconnect();
        }
      },
      // Height-independent "reached": fires when the section's top crosses ~75%
      // down the viewport, regardless of how tall the section is.
      { threshold: 0, rootMargin: '0px 0px -25% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [name]);

  return <div ref={ref}>{children}</div>;
}
